import { useState, useEffect, useRef } from 'react';
import { getOrGenerateMicroInteraction, flagMicroInteraction } from '../services/conceptPlus1Service';
import { logMasteryEvent, getPhaseAttemptCounts, type ConfidenceLevel } from '../services/masteryService';
import ChinniExplainer from './ChinniExplainer';

type Step = 'loading' | 'predict' | 'reflect' | 'done';
type AccentColor = 'green' | 'amber' | 'blue';

interface MicroQuestion {
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string;
}

interface SceneFact { label: string; value: string; }
interface Scene { icon: string; facts: SceneFact[]; }

export interface SingleMicroInteraction {
  scene?: Scene;
  predict: MicroQuestion;
  reflect: MicroQuestion & { explanation: string };
}

interface Props {
  conceptId: number;
  conceptName: string;
  subject: string;
  mode: 'real_life' | 'tips' | 'formula';
  contentText: string;
  gradeBand: string;
  label?: string;
  accentColor: AccentColor;
  conceptTable?: 'concepts' | 'concepts_plus1';
  categoryKey?: string;
  studentId?: string;
  onPredictComplete?: () => void;
  onComplete?: () => void;
  onAwardBadge?: (criteria: string) => void;
}

const ACCENT: Record<AccentColor, {
  border: string; bg: string; sceneBg: string; text: string;
  activeDot: string; hoverBtn: string; correctBg: string; wrongBg: string;
}> = {
  green: {
    border: 'border-green-200', bg: 'bg-white', sceneBg: 'bg-green-50', text: 'text-green-700',
    activeDot: 'bg-green-500', hoverBtn: 'hover:border-green-400 hover:bg-green-50',
    correctBg: 'border-green-500 bg-green-50 text-green-800',
    wrongBg: 'border-red-400 bg-red-50 text-red-800',
  },
  amber: {
    border: 'border-amber-200', bg: 'bg-white', sceneBg: 'bg-amber-50', text: 'text-amber-700',
    activeDot: 'bg-amber-500', hoverBtn: 'hover:border-amber-400 hover:bg-amber-50',
    correctBg: 'border-green-500 bg-green-50 text-green-800',
    wrongBg: 'border-red-400 bg-red-50 text-red-800',
  },
  blue: {
    border: 'border-blue-200', bg: 'bg-white', sceneBg: 'bg-blue-50', text: 'text-blue-700',
    activeDot: 'bg-blue-500', hoverBtn: 'hover:border-blue-400 hover:bg-blue-50',
    correctBg: 'border-green-500 bg-green-50 text-green-800',
    wrongBg: 'border-red-400 bg-red-50 text-red-800',
  },
};

const CONFIDENCE_OPTIONS: { level: ConfidenceLevel; emoji: string; label: string }[] = [
  { level: 'low',    emoji: '😕', label: 'Not sure' },
  { level: 'medium', emoji: '🤔', label: 'Somewhat' },
  { level: 'high',   emoji: '😊', label: 'Confident' },
];

function ConfidenceTap({
  onTap,
  tapped,
}: {
  onTap: (level: ConfidenceLevel) => void;
  tapped: ConfidenceLevel | null;
}) {
  return (
    <div className="flex items-center gap-2 mt-2">
      <span className="text-xs text-gray-400">How confident were you?</span>
      <div className="flex gap-1">
        {CONFIDENCE_OPTIONS.map(({ level, emoji, label }) => (
          <button
            key={level}
            onClick={() => !tapped && onTap(level)}
            disabled={!!tapped}
            title={label}
            className={`text-lg leading-none transition-all ${
              tapped
                ? tapped === level
                  ? 'opacity-100 scale-110'
                  : 'opacity-30'
                : 'hover:scale-125 active:scale-110'
            }`}
          >
            {tapped === level ? '✓' : emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MicroInteractionCard({
  conceptId, conceptName, subject, mode, contentText, gradeBand,
  label, accentColor, conceptTable = 'concepts', categoryKey, studentId,
  onPredictComplete, onComplete, onAwardBadge,
}: Props) {
  const [step, setStep]               = useState<Step>('loading');
  const [interaction, setInteraction] = useState<SingleMicroInteraction | null>(null);
  const [error, setError]             = useState(false);
  const [predictIdx, setPredictIdx]   = useState<number | null>(null);
  const [reflectIdx, setReflectIdx]   = useState<number | null>(null);
  const [sceneCollapsed, setSceneCollapsed] = useState(false);
  const [predictConfidence, setPredictConfidence] = useState<ConfidenceLevel | null>(null);
  const [reflectConfidence, setReflectConfidence] = useState<ConfidenceLevel | null>(null);
  const [mistakeCategory, setMistakeCategory]     = useState<string | null>(null);
  const [discernmentAnswer, setDiscernmentAnswer] = useState<string | null>(null);
  const [showCorrectGlow, setShowCorrectGlow]     = useState(false);
  const [priorAttempts, setPriorAttempts]         = useState<{ predict: number; reflect: number } | null>(null);
  const c = ACCENT[accentColor];
  const correctStreak = useRef(0);

  // Timestamp refs (don't trigger re-renders)
  const predictShownAt   = useRef<Date | null>(null);
  const predictAnswered  = useRef<Date | null>(null);
  const reflectShownAt   = useRef<Date | null>(null);
  const reflectAnswered  = useRef<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStep('loading');
    setError(false);
    setInteraction(null);
    setPredictIdx(null);
    setReflectIdx(null);
    setSceneCollapsed(false);
    setPredictConfidence(null);
    setReflectConfidence(null);
    setMistakeCategory(null);
    setDiscernmentAnswer(null);
    setShowCorrectGlow(false);
    predictShownAt.current = null;
    predictAnswered.current = null;
    reflectShownAt.current = null;
    reflectAnswered.current = null;

    getOrGenerateMicroInteraction({ conceptId, conceptName, subject, mode, contentText, gradeBand, conceptTable })
      .then(result => {
        if (cancelled) return;
        if (result.error || !result.interaction_json) { setError(true); return; }
        let data = result.interaction_json as Record<string, unknown>;
        if (categoryKey && data[categoryKey]) {
          data = data[categoryKey] as Record<string, unknown>;
        }
        if (!data.predict) {
          const nested = Object.values(data).find(
            v => v && typeof v === 'object' && (v as Record<string, unknown>).predict
          );
          if (nested) data = nested as Record<string, unknown>;
        }
        if (!data.predict) { setError(true); return; }
        setInteraction(data as unknown as SingleMicroInteraction);
        setStep('predict');
        predictShownAt.current = new Date();
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [conceptId, mode, contentText, categoryKey]);

  useEffect(() => {
    if (studentId) {
      getPhaseAttemptCounts(studentId, conceptId).then(setPriorAttempts);
    }
  }, [studentId, conceptId]);

  if (error) return null;

  const stepDots = ['predict', 'reflect', 'done'] as const;

  if (step === 'loading') return (
    <div className={`rounded-lg border ${c.border} overflow-hidden animate-pulse`}>
      <div className="flex flex-col md:flex-row">
        <div className={`hidden md:block md:w-2/5 ${c.sceneBg} p-4`}>
          <div className="w-10 h-10 bg-gray-200 rounded-full mx-auto mb-3" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-full" />
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
        <div className="flex-1 p-4 space-y-3">
          <div className="flex gap-1 mb-1">
            {[0,1,2].map(i => <div key={i} className="w-2 h-2 rounded-full bg-gray-200" />)}
          </div>
          <div className="h-4 bg-gray-200 rounded w-4/5" />
          <div className="h-4 bg-gray-200 rounded w-3/5" />
          <div className="space-y-2 pt-1">
            {[0,1,2].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg border border-gray-200" />)}
          </div>
        </div>
      </div>
    </div>
  );

  if (!interaction) return null;

  const scene = interaction.scene;
  const hasScene = !!(scene?.facts?.length);

  const handlePredict = (idx: number) => {
    predictAnswered.current = new Date();
    setPredictIdx(idx);
    setStep('reflect');
    setSceneCollapsed(true);
    reflectShownAt.current = new Date();
    onPredictComplete?.();
    onAwardBadge?.('first_predict');
  };

  const handleReflect = (idx: number) => {
    reflectAnswered.current = new Date();
    setReflectIdx(idx);
    setStep('done');
    const isCorrect = idx === interaction!.reflect.correct_index;
    if (isCorrect) {
      setShowCorrectGlow(true);
      setTimeout(() => setShowCorrectGlow(false), 800);
      correctStreak.current += 1;
      if (correctStreak.current >= 3) onAwardBadge?.('correct_streak_3');
    } else {
      correctStreak.current = 0;
    }
    onComplete?.();
    onAwardBadge?.('micro_complete');
  };

  const handlePredictConfidence = (level: ConfidenceLevel) => {
    setPredictConfidence(level);
    if (!studentId || predictIdx === null || !interaction) return;
    logMasteryEvent({
      studentId,
      conceptId,
      eventType: 'micro_predict',
      correct: predictIdx === interaction.predict.correct_index,
      confidence: level,
      interactionStartedAt: predictShownAt.current,
      firstResponseAt: predictAnswered.current,
      finalSubmissionAt: predictAnswered.current,
    });
  };

  const handleReflectConfidence = (level: ConfidenceLevel) => {
    setReflectConfidence(level);
    if (!studentId || reflectIdx === null || !interaction) return;
    logMasteryEvent({
      studentId,
      conceptId,
      eventType: 'micro_reflect',
      correct: reflectIdx === interaction.reflect.correct_index,
      confidence: level,
      mistakeCategory: mistakeCategory ?? null,
      interactionStartedAt: reflectShownAt.current,
      firstResponseAt: reflectAnswered.current,
      finalSubmissionAt: reflectAnswered.current,
    });
  };

  const handleDiscernmentTap = (answer: string) => {
    if (discernmentAnswer) return;
    setDiscernmentAnswer(answer);
    if (answer === 'Seems wrong') {
      flagMicroInteraction(conceptId, gradeBand, mode, conceptTable);
    }
    if (!studentId) return;
    logMasteryEvent({
      studentId,
      conceptId,
      eventType: 'discernment_check',
      correct: answer === 'Seems wrong',
    });
  };

  const handleReset = () => {
    setStep('predict');
    setPredictIdx(null);
    setReflectIdx(null);
    setSceneCollapsed(false);
    setPredictConfidence(null);
    setReflectConfidence(null);
    setMistakeCategory(null);
    setDiscernmentAnswer(null);
    setShowCorrectGlow(false);
    predictShownAt.current = new Date();
    predictAnswered.current = null;
    reflectShownAt.current = null;
    reflectAnswered.current = null;
  };

  // Negative marking risk: student was confident AND got it wrong
  const predictRisky = predictConfidence === 'high' && predictIdx !== null && predictIdx !== interaction.predict.correct_index;
  const reflectRisky = reflectConfidence === 'high' && reflectIdx !== null && reflectIdx !== interaction.reflect.correct_index;

  return (
    <div className={`rounded-lg border ${c.border} overflow-hidden`}>
      {label && (
        <div className={`px-4 pt-3 pb-1 ${c.sceneBg} border-b ${c.border}`}>
          <p className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>{label}</p>
        </div>
      )}

      <div className={`flex ${hasScene ? 'flex-col md:flex-row' : 'flex-col'}`}>

        {/* Scene panel */}
        {hasScene && (
          <>
            <div className={`${sceneCollapsed ? 'flex' : 'hidden'} md:hidden items-center gap-2 px-3 py-2 border-b ${c.border} ${c.sceneBg}`}>
              <span className="text-lg leading-none">{scene!.icon}</span>
              <span className="text-xs text-gray-500 truncate">
                {scene!.facts[0]?.label}: <span className="font-semibold text-gray-700">{scene!.facts[0]?.value}</span>
              </span>
            </div>

            <div className={`
              md:w-2/5 md:sticky md:top-4 md:self-start md:border-b-0 md:border-r
              border-b ${c.border} ${c.sceneBg} p-4 flex-shrink-0
              ${sceneCollapsed ? 'hidden md:block' : 'block'}
            `}>
              <div className="text-3xl text-center mb-3 leading-none select-none">{scene!.icon}</div>
              <div className="relative max-h-36 overflow-y-auto">
                <div className="space-y-0">
                  {scene!.facts.map((f, i) => (
                    <div key={i} className={`flex justify-between items-start gap-3 py-1.5 text-xs ${i < scene!.facts.length - 1 ? `border-b ${c.border}` : ''}`}>
                      <span className="text-gray-500 flex-shrink-0">{f.label}</span>
                      <span className="font-semibold text-gray-800 text-right">{f.value}</span>
                    </div>
                  ))}
                </div>
                <div className={`pointer-events-none sticky bottom-0 h-4 bg-gradient-to-t from-[${accentColor === 'green' ? '#f0fdf4' : accentColor === 'amber' ? '#fffbeb' : '#eff6ff'}] to-transparent`} />
              </div>
            </div>
          </>
        )}

        {/* Question panel */}
        <div className={`flex-1 p-4 flex flex-col gap-3 ${c.bg}`}>
          {/* Step progress dots */}
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
              {stepDots.map((s, i) => (
                <div key={s} className="flex items-center gap-1">
                  <div className={`rounded-full transition-all ${
                    step === s
                      ? `w-3 h-3 ${c.activeDot}`
                      : stepDots.indexOf(step) > i
                      ? 'w-2 h-2 bg-gray-300'
                      : 'w-2 h-2 bg-gray-200'
                  }`} />
                  {i < 2 && <div className="w-4 h-px bg-gray-200" />}
                </div>
              ))}
              <span className="ml-1 text-xs text-gray-400">
                {step === 'predict' ? 'Predict' : step === 'reflect' ? 'Reflect' : 'Done'}
              </span>
            </div>
            {(priorAttempts?.predict ?? 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                Attempt {(priorAttempts?.predict ?? 0) + 1}
              </span>
            )}
          </div>

          {/* Predict */}
          {step === 'predict' && (
            <>
              <p className="text-sm font-medium text-gray-800 leading-relaxed">{interaction.predict.question}</p>
              <div className="space-y-2">
                {interaction.predict.options.map((opt, i) => (
                  <button key={i} onClick={() => handlePredict(i)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm leading-snug ${c.hoverBtn} transition-colors`}>
                    {opt}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Reflect */}
          {step === 'reflect' && (
            <>
              {predictIdx !== null && (
                <div className={`text-xs px-3 py-1.5 rounded border ${
                  predictIdx === interaction.predict.correct_index ? c.correctBg : c.wrongBg
                }`}>
                  {predictIdx === interaction.predict.correct_index
                    ? '✓ Correct!'
                    : `✗ Answer: ${interaction.predict.options[interaction.predict.correct_index]}`}
                </div>
              )}

              {/* Confidence tap for predict — captured after seeing the answer */}
              <ConfidenceTap onTap={handlePredictConfidence} tapped={predictConfidence} />

              {/* Negative marking coaching — high confidence + wrong */}
              {predictRisky && (
                <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800">
                  <span className="text-base leading-none flex-shrink-0">⚠️</span>
                  <span>High confidence + wrong = −4 marks in JEE. This pattern needs attention.</span>
                </div>
              )}

              <p className="text-sm font-medium text-gray-800 leading-relaxed">{interaction.reflect.question}</p>
              <div className="space-y-2">
                {interaction.reflect.options.map((opt, i) => (
                  <button key={i} onClick={() => handleReflect(i)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm leading-snug ${c.hoverBtn} transition-colors`}>
                    {opt}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Done */}
          {step === 'done' && reflectIdx !== null && (
            <>
              {/* Wrong answer: self-categorisation gate (shown before explanation) */}
              {reflectIdx !== interaction.reflect.correct_index && !mistakeCategory && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Why did you get that wrong?</p>
                  {["Silly mistake", "Didn't understand", "Had no idea"].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setMistakeCategory(cat)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {/* Explanation shown once correct or category selected */}
              {(reflectIdx === interaction.reflect.correct_index || mistakeCategory !== null) && (
                <>
                  <div className={`text-sm px-3 py-2.5 rounded border leading-relaxed transition-all ${
                    reflectIdx === interaction.reflect.correct_index
                      ? `${c.correctBg}${showCorrectGlow ? ' correct-glow' : ''}`
                      : c.wrongBg
                  }`}>
                    <span className="font-semibold mr-1">
                      {reflectIdx === interaction.reflect.correct_index ? '✓ Correct!' : '✗ Not quite.'}
                    </span>
                    {interaction.reflect.explanation}
                  </div>
                  {/* Discernment tap */}
                  {!discernmentAnswer ? (
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-400">Does this explanation make sense?</p>
                      <div className="flex items-center gap-2">
                        {['Yes', 'Partly', 'Seems wrong'].map((ans) => (
                          <button
                            key={ans}
                            onClick={() => handleDiscernmentTap(ans)}
                            className="px-3 py-1 text-xs rounded-full border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600"
                          >
                            {ans}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-300">✓ Thanks</p>
                  )}
                  <ChinniExplainer
                    question={interaction.reflect.question}
                    optionA={interaction.reflect.options[0]}
                    optionB={interaction.reflect.options[1]}
                    optionC={interaction.reflect.options[2]}
                    optionD={interaction.reflect.options[3]}
                    correctIndex={interaction.reflect.correct_index}
                    conceptName={conceptName}
                    gradeLevel={Number(gradeBand.replace(/\D/g, '')) || 11}
                  />
                  {/* Confidence tap for reflect */}
                  <ConfidenceTap onTap={handleReflectConfidence} tapped={reflectConfidence} />
                  {/* Negative marking coaching for reflect */}
                  {reflectRisky && (
                    <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800">
                      <span className="text-base leading-none flex-shrink-0">⚠️</span>
                      <span>Even with all the context, high confidence + wrong is a risky pattern for −4 mark penalties.</span>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Try again — pinned to bottom */}
          {step === 'done' && (
            <div className="mt-auto pt-2">
              <button
                onClick={handleReset}
                className={`text-xs font-medium ${c.text} hover:underline transition-colors`}>
                Try again ↺
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
